import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  ChargerStatus,
  EvChargerCloudStatus,
  EvChargerData,
  EvChargerDetails,
  EvChargerStatusData,
  EvChargerWifiDetails,
  GenericStatusResponse,
} from 'clipsal-cortex-types/src/api/api-evcharger';

import { AppThunk, RootState } from '../../../app/store';
import { del, get, post } from '../../../common/api/api-helpers';
import { IS_PRODUCTION } from '../../../common/constants';
import { fetchSite, removeSiteDevice } from '../../site/siteSlice';
import { clearLiveChargingReadings, setAmps, setKwhConsumed, setSmartAmpState } from '../../smart-amp/evseStreamSlice';
import { parseInfo, SmartAmpSseState } from '../../smart-amp/smart-amp-sse-helpers';
import { EvseVariant, getEvseTypeForVariant, resolveEvseVariant } from '../evse-variant';
import { EvseType } from '../types';
import availableStatus from './simulated-statuses/charger_status_available.json';
import chargerErrorStatus from './simulated-statuses/charger_status_charger_error.json';
import chargingAutomaticStatus from './simulated-statuses/charger_status_charging_automatic.json';
import chargingDeratedSmartAmpStatus from './simulated-statuses/charger_status_charging_derated_smartamp.json';
import chargingManualStatus from './simulated-statuses/charger_status_charging_manual.json';
import evStoppedChargingStatus from './simulated-statuses/charger_status_ev_stopped_charging.json';
import evseStoppedSmartAmpStatus from './simulated-statuses/charger_status_evse_stopped_smartamp.json';
import offlineStatus from './simulated-statuses/charger_status_offline.json';
import userStoppedChargingStatus from './simulated-statuses/charger_status_user_stopped_charging.json';
import waitingScheduledStatus from './simulated-statuses/charger_status_waiting_scheduled.json';

export type ChargeSimulationType =
  | ChargerStatus
  | 'Wifi_Faulted'
  | 'None'
  | `${ChargerStatus}_SmartAmp`
  | 'Wifi_Faulted_SmartAmp'
  | 'Suspended_EVSE_SmartAmp'
  | `${ChargerStatus}_SmartPlug`
  | 'Wifi_Faulted_SmartPlug'
  | 'Suspended_EVSE_SmartPlug';

export type DeferralType = 'load' | 'temp' | 'both';

export type FirmwareSimulationType =
  | 'None'
  | 'Pending'
  | 'Downloading'
  | 'Installing'
  | 'Success'
  | 'DownloadFailed'
  | 'InstallationFailed'
  | 'FailedChargerNotConnected';

export const COMMISSIONING_STARTED_AT_STORAGE_KEY = 'evse_commissioning_started_at';

function readCommissioningStartedAtFromStorage(): number | undefined {
  if (typeof window === 'undefined') return undefined;
  const raw = window.localStorage.getItem(COMMISSIONING_STARTED_AT_STORAGE_KEY);
  if (!raw) return undefined;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export type SimulatedEntry = EvChargerStatusData & {
  info?: string | null;
  kwhConsumed?: number;
  maxAmps?: number | null;
  chargingAmps?: number | null;
};

const DEFERRED_SIMULATION_BASES = ['Unavailable_Disconnect', 'Suspended_EVSE'];

export const EVSE_SIMULATION_SUFFIX: Record<EvseType, '_SmartAmp' | '_SmartPlug'> = {
  SmartAmp: '_SmartAmp',
  SmartPlug: '_SmartPlug',
};

const EVSE_SIMULATION_SUFFIX_REGEX = /_(SmartAmp|SmartPlug)$/;

/** Simulation key without its EVSE-type suffix, e.g. `Suspended_EVSE_SmartPlug` to `Suspended_EVSE`. */
export function getSimulationBase(chargeSimulationType: string): string {
  return chargeSimulationType.replace(EVSE_SIMULATION_SUFFIX_REGEX, '');
}

export function isDeviceSimulationType(chargeSimulationType: string): boolean {
  return EVSE_SIMULATION_SUFFIX_REGEX.test(chargeSimulationType);
}

/** Re-targets a device-specific simulation key to the given EVSE type, and plain keys are returned unchanged. */
export function toEvseSimulationType(chargeSimulationType: ChargeSimulationType, evseType: EvseType) {
  if (!isDeviceSimulationType(chargeSimulationType)) return chargeSimulationType;
  return `${getSimulationBase(chargeSimulationType)}${EVSE_SIMULATION_SUFFIX[evseType]}` as ChargeSimulationType;
}

/**
 * Whether the charger's load-management device (SmartAmp or SmartPlug, per `evseType`) is active,
 * from live SSE derating flags or while simulating the device-specific simulation key.
 */
export function deriveEvseDeviceActive(
  evseType: EvseType,
  chargeSimulationType: ChargeSimulationType,
  loadActive: boolean,
  tempActive: boolean
): { isSmartAmpActive: boolean; isSmartPlugActive: boolean } {
  const isActive = loadActive || tempActive || chargeSimulationType.endsWith(EVSE_SIMULATION_SUFFIX[evseType]);
  return {
    isSmartAmpActive: evseType === 'SmartAmp' && isActive,
    isSmartPlugActive: evseType === 'SmartPlug' && isActive,
  };
}

const deferralInfoMap: Record<DeferralType, string> = {
  load: 'DeratingSUA=1;DeratingThermal=0',
  temp: 'DeratingSUA=0;DeratingThermal=1',
  both: 'DeratingSUA=1;DeratingThermal=1',
};

export function getSimulatedEvChargerStatus(
  chargeSimulationType: ChargeSimulationType,
  deferralType: DeferralType
): SimulatedEntry | undefined {
  const statusToSimulatedResponse: Partial<Record<string, SimulatedEntry>> = {
    // No SmartAmp / SmartPlug
    Available: availableStatus as SimulatedEntry,
    Preparing: chargingAutomaticStatus as SimulatedEntry, // Charging Ongoing (Automatic)
    Charging: chargingManualStatus as SimulatedEntry, // Charging Ongoing (Manual)
    Finishing: waitingScheduledStatus as SimulatedEntry, // Waiting for Scheduled Charging to Start
    SuspendedEVSE: userStoppedChargingStatus as SimulatedEntry, // User Stopped Charging from SE Home App
    SuspendedEV: evStoppedChargingStatus as SimulatedEntry, // EV Stopped Charging
    // Device (SmartAmp / SmartPlug) always on for Charging Derated and EVSE Stopped
    Unavailable_Disconnect: chargingDeratedSmartAmpStatus as SimulatedEntry, // Charging Derated
    Suspended_EVSE: evseStoppedSmartAmpStatus as SimulatedEntry, // EVSE Stopped by SmartAmp / SmartPlug
    // SmartAmp always on
    Faulted: chargerErrorStatus as SimulatedEntry, // Charger Error (SmartAmp always active)
    Wifi_Faulted: offlineStatus as SimulatedEntry, // Charger Offline
  };

  const isDeviceType = isDeviceSimulationType(chargeSimulationType);
  const base = getSimulationBase(chargeSimulationType);
  const isDeferredBase = DEFERRED_SIMULATION_BASES.includes(base);
  // Deferred fixtures exist only as `_SmartAmp` / `_SmartPlug` keys, all others only without a suffix.
  const simulated = isDeviceType === isDeferredBase ? statusToSimulatedResponse[base] : undefined;

  if (!simulated) return undefined;
  return isDeviceType && isDeferredBase ? { ...simulated, info: deferralInfoMap[deferralType] } : simulated;
}

export function getSimulatedSmartAmpState(
  chargeSimulationType: ChargeSimulationType,
  deferralType: DeferralType
): SmartAmpSseState {
  const simulated = getSimulatedEvChargerStatus(chargeSimulationType, deferralType);
  const { loadActive, tempActive } = parseInfo(simulated?.info ?? null);
  const anyActive = loadActive || tempActive;
  return {
    loadActive,
    tempActive,
    status: anyActive ? (simulated?.status === 'Charging' ? 'active' : 'chargingLimited') : 'inactive',
  };
}

/**
 * Stores the SmartAmp derating state. While a simulation is active the simulated state wins,
 * so live SSE events (whose `info` reflects the real charger) can't overwrite it.
 */
export const syncSmartAmpState =
  (liveState?: SmartAmpSseState): AppThunk =>
  (dispatch, getState) => {
    const { chargeSimulationType, deferralType } = getState().evCharger;
    if (chargeSimulationType !== 'None') {
      dispatch(setSmartAmpState(getSimulatedSmartAmpState(chargeSimulationType, deferralType)));
      const simulated = getSimulatedEvChargerStatus(chargeSimulationType, deferralType);
      dispatch(setAmps({ maxAmps: simulated?.maxAmps ?? null, chargingAmps: simulated?.chargingAmps ?? null }));
    } else if (liveState) {
      dispatch(setSmartAmpState(liveState));
    }
  };

/**
 * MeterValues aren't guaranteed to send a final zero sample, so a confirmed non-charging status
 * (status update or StopTransaction) clears cached live power and charging current.
 */
export const syncLiveChargingReadings =
  (liveStatus: ChargerStatus): AppThunk =>
  (dispatch, getState) => {
    if (getState().evCharger.chargeSimulationType !== 'None' || liveStatus === 'Charging') return;
    dispatch(clearLiveChargingReadings());
  };

export type EvChargerState = {
  isLoaded: boolean;
  isUpdating: boolean;
  // Status shown when a start/stop command was sent. The skeleton stays up until an SSE status differs from it.
  pendingCommandFromStatus: ChargerStatus | null;
  pendingCommandDeadline: number | null;
  isDeactivating: boolean;
  hasVisitedRegisterScreen: boolean;
  hasFetchedEvChargerDetails: boolean;
  siteDeviceId: string;
  wifiSsid: string | null;
  chargeSimulationType: ChargeSimulationType;
  firmwareSimulationType: FirmwareSimulationType;
  tariffSimulationActive: boolean;
  deferralType: DeferralType;
  evseVariant: EvseVariant;
  // Derived from `evseVariant` when known; the simulation developer tool may override it.
  evseType: EvseType;
  status: ChargerStatus;
  vendorErrorCode: string | null;
  // Timestamp (ms) when commissioning started, used to calculate 5-minute timeout.
  // Used to show commissioning UI until first SSE status event or timeout.
  commissioningStartedAt?: number;
} & EvChargerStatusData &
  Pick<EvChargerDetails, 'description' | 'firmware' | 'model' | 'output' | 'sn' | 'charger_id'>;

export const fetchEvChargerDetails = createAsyncThunk<EvChargerDetails, string, { state: RootState }>(
  'evCharger/fetchEvChargerDetails',
  async (siteDeviceId) => {
    const chargerDetails = await get<EvChargerDetails>(`/v1/ev_chargers/${siteDeviceId}`);

    try {
      const cloudStatus = await get<EvChargerCloudStatus>(`/v1/ev_chargers/${chargerDetails.charger_id}/cloud_status`);

      if (cloudStatus.cloud_status === 'Sandbox') {
        return { ...chargerDetails, status: 'Unavailable_Disconnect' };
      }
    } catch {
      // Cloud status is best-effort; fall back to charger details if the endpoint fails.
    }

    return chargerDetails;
  }
);

export const fetchEvChargerStatus = createAsyncThunk<EvChargerStatusData, string, { state: RootState }>(
  'evCharger/fetchEvChargerStatus',
  async (siteDeviceId, { getState, dispatch }) => {
    const state = getState();

    if (state.evCharger.chargeSimulationType !== 'None') {
      const effectiveSimulated = getSimulatedEvChargerStatus(
        state.evCharger.chargeSimulationType,
        state.evCharger.deferralType
      );
      if (!effectiveSimulated) {
        throw new Error(`Unsupported charge simulation: ${state.evCharger.chargeSimulationType}`);
      }
      dispatch(
        setSmartAmpState(getSimulatedSmartAmpState(state.evCharger.chargeSimulationType, state.evCharger.deferralType))
      );
      dispatch(setKwhConsumed(effectiveSimulated.kwhConsumed ?? 0));
      dispatch(
        setAmps({
          maxAmps: effectiveSimulated.maxAmps ?? null,
          chargingAmps: effectiveSimulated.chargingAmps ?? null,
        })
      );
      return effectiveSimulated;
    }

    return await get<EvChargerStatusData>(`/v1/ev_chargers/${siteDeviceId}/status`);
  }
);

export const deactivateEvCharger = createAsyncThunk<GenericStatusResponse, string, { state: RootState }>(
  'evCharger/deactivateEvCharger',
  async (siteDeviceId, { getState, dispatch }) => {
    const response = await del<GenericStatusResponse>(`/v1/ev_chargers/${siteDeviceId}`, {});
    dispatch(removeSiteDevice(Number(siteDeviceId)));

    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(COMMISSIONING_STARTED_AT_STORAGE_KEY);
    }

    // After deactivation, refetch site data to update the state and exit the dashboard if the device is removed.
    const state = getState();
    dispatch(fetchSite({ siteID: state.site.site_id, isProduction: IS_PRODUCTION }));
    return response;
  }
);

export const fetchEvChargerWifiDetails = createAsyncThunk<EvChargerWifiDetails, string, { state: RootState }>(
  'evCharger/fetchEvChargerWifiDetails',
  async (chargerId) => {
    return await get<EvChargerWifiDetails>(`/v1/ev_chargers/${chargerId}/wifi_details`);
  }
);

export const startCharging = createAsyncThunk<GenericStatusResponse, string, { state: RootState }>(
  'evCharger/startCharging',
  async (siteDeviceId) => {
    return await post<GenericStatusResponse>(`/v1/ev_chargers/${siteDeviceId}/start_charge`, {});
  }
);

export const stopCharging = createAsyncThunk<GenericStatusResponse, string, { state: RootState }>(
  'evCharger/stopCharging',
  async (siteDeviceId) => {
    return await post<GenericStatusResponse>(`/v1/ev_chargers/${siteDeviceId}/stop_charge`, {});
  }
);

export const COMMAND_CONFIRMATION_TIMEOUT_MS = 10_000;

export const initialState: EvChargerState = {
  isLoaded: false,
  isUpdating: false,
  pendingCommandFromStatus: null,
  pendingCommandDeadline: null,
  hasVisitedRegisterScreen: false,
  hasFetchedEvChargerDetails: false,
  isDeactivating: false,
  siteDeviceId: '',
  wifiSsid: null,
  charger_id: '',
  error: 'NoError',
  error_message: '',
  error_title: '',
  vendorErrorCode: null,
  status: 'Unavailable_Disconnect',
  commissioningStartedAt: readCommissioningStartedAtFromStorage(),
  latest_charge_session_start: '',
  latest_charge_session_end: '',
  session_time: 0,
  energy_consumed: 0,
  power: 0,
  timestamp: '',
  description: '',
  firmware: '',
  model: '',
  output: '',
  sn: '',
  chargeSimulationType: 'None',
  firmwareSimulationType: 'None',
  tariffSimulationActive: false,
  deferralType: 'load',
  evseVariant: 'Unknown',
  evseType: 'SmartAmp',
  clipsal_endpoints: [
    'retrieve',
    'cloud_status',
    'start_charge',
    'pause_charge',
    'resume_charge',
    'stop_charge',
    'get_status',
    'get_charge_current_request',
    'set_charge_current',
    'clear_charge_current',
    'history',
  ],
  charge_current: null,
  charge_current_request: null,
  charge_current_request_max: null,
  battery_level: null,
  is_scheduled_charge_active: null,
  scheduled_charge_start_time: null,
  scheduled_charge_end_time: null,
  estimated_full_charge_time: null,
  timeout_seconds: null,
  control_mode: 'Automatic',
};

/**
 * State changes for a resolved variant. `Unknown` never overrides a known variant, and an active
 * simulation keeps its manually selected EVSE type.
 */
function getEvseVariantUpdate(state: EvChargerState, evseVariant: EvseVariant): Partial<EvChargerState> {
  const evseType = getEvseTypeForVariant(evseVariant);
  if (!evseType) return {};
  if (state.chargeSimulationType !== 'None') return { evseVariant };
  return { evseVariant, evseType };
}

export const evChargerSlice = createSlice({
  name: 'evCharger',
  initialState,
  reducers: {
    resetEvChargerData: () => {
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(COMMISSIONING_STARTED_AT_STORAGE_KEY);
      }
      return { ...initialState, commissioningStartedAt: undefined };
    },
    setIsLoaded: (state, action: PayloadAction<boolean>) => {
      return { ...state, isLoaded: action.payload };
    },
    setSiteDeviceId: (state, action: PayloadAction<string>) => {
      return { ...state, siteDeviceId: action.payload };
    },
    addEnrolledEvChargerDetails: (
      state,
      action: PayloadAction<
        Partial<EvChargerData> & {
          commissioningStartedAt?: number;
        }
      >
    ) => {
      return {
        ...state,
        ...action.payload,
        model: action.payload.model ?? state.model,
        sn: action.payload.sn ?? state.sn,
        isLoaded: false,
        commissioningStartedAt: action.payload.commissioningStartedAt ?? state.commissioningStartedAt,
      };
    },
    setHasVisitedRegisterScreen: (state, action) => {
      return { ...state, hasVisitedRegisterScreen: action.payload };
    },
    setChargeSimulationType: (state, action: PayloadAction<string>) => {
      return { ...state, chargeSimulationType: action.payload as ChargeSimulationType };
    },
    setTariffSimulationActive: (state, action: PayloadAction<boolean>) => {
      return { ...state, tariffSimulationActive: action.payload };
    },
    setDeferralType: (state, action: PayloadAction<DeferralType>) => {
      return { ...state, deferralType: action.payload };
    },
    setFirmwareSimulationType: (state, action: PayloadAction<FirmwareSimulationType>) => {
      return { ...state, firmwareSimulationType: action.payload };
    },
    setEvseType: (state, action: PayloadAction<EvseType>) => {
      return {
        ...state,
        evseType: action.payload,
        chargeSimulationType: toEvseSimulationType(state.chargeSimulationType, action.payload),
      };
    },
    setEvseVariant: (state, action: PayloadAction<EvseVariant>) => {
      return { ...state, ...getEvseVariantUpdate(state, action.payload) };
    },
    updateStatusFromSse: (
      state,
      action: PayloadAction<{
        status: ChargerStatus;
        error: EvChargerStatusData['error'];
        vendorErrorCode?: string | null;
        control_mode?: EvChargerStatusData['control_mode'];
        is_scheduled_charge_active?: EvChargerStatusData['is_scheduled_charge_active'];
        scheduled_charge_start_time?: EvChargerStatusData['scheduled_charge_start_time'];
        scheduled_charge_end_time?: EvChargerStatusData['scheduled_charge_end_time'];
      }>
    ) => {
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(COMMISSIONING_STARTED_AT_STORAGE_KEY);
      }
      // When simulating, apply the simulated status instead of the live SSE payload.
      if (state.chargeSimulationType !== 'None') {
        const simulated = getSimulatedEvChargerStatus(state.chargeSimulationType, state.deferralType);
        const hasCommissioningAnchor = state.commissioningStartedAt !== undefined;
        return {
          ...state,
          ...simulated,
          ...(hasCommissioningAnchor && { commissioningStartedAt: undefined }),
          pendingCommandFromStatus: null,
          pendingCommandDeadline: null,
          isLoaded: true,
        };
      }
      // First SSE status update ends the commissioning-wait phase.
      const hasCommissioningAnchor = state.commissioningStartedAt !== undefined;
      const isErrorCleared = action.payload.error === 'NoError';
      const isAwaitingCommandStatus =
        state.pendingCommandFromStatus !== null && action.payload.status === state.pendingCommandFromStatus;
      return {
        ...state,
        status: action.payload.status,
        error: action.payload.error,
        ...(isErrorCleared
          ? { error_title: '', error_message: '', vendorErrorCode: null }
          : {
              ...(action.payload.vendorErrorCode !== undefined && { vendorErrorCode: action.payload.vendorErrorCode }),
            }),
        ...(action.payload.control_mode !== undefined && { control_mode: action.payload.control_mode }),
        ...(action.payload.is_scheduled_charge_active !== undefined && {
          is_scheduled_charge_active: action.payload.is_scheduled_charge_active,
        }),
        ...(action.payload.scheduled_charge_start_time !== undefined && {
          scheduled_charge_start_time: action.payload.scheduled_charge_start_time,
        }),
        ...(action.payload.scheduled_charge_end_time !== undefined && {
          scheduled_charge_end_time: action.payload.scheduled_charge_end_time,
        }),
        ...(hasCommissioningAnchor && { commissioningStartedAt: undefined }),
        pendingCommandFromStatus: isAwaitingCommandStatus ? state.pendingCommandFromStatus : null,
        pendingCommandDeadline: isAwaitingCommandStatus ? state.pendingCommandDeadline : null,
        isLoaded: !isAwaitingCommandStatus,
      };
    },
    commandConfirmationTimedOut: (state) => {
      if (state.pendingCommandFromStatus === null) return state;
      return { ...state, pendingCommandFromStatus: null, pendingCommandDeadline: null, isLoaded: true };
    },
    updateControlModeFromSse: (
      state,
      action: PayloadAction<{
        control_mode: EvChargerStatusData['control_mode'];
      }>
    ) => {
      return {
        ...state,
        control_mode: action.payload.control_mode,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEvChargerDetails.fulfilled, (state, { payload }) => {
        // We do not require last_status as of now.
        const { last_status: lastStatus, ...chargerDetails } = payload;
        return {
          ...state,
          ...chargerDetails,
          ...getEvseVariantUpdate(state, resolveEvseVariant(chargerDetails.model)),
          hasFetchedEvChargerDetails: true,
        };
      })
      .addCase(fetchEvChargerDetails.rejected, (state) => {
        return { ...state, hasFetchedEvChargerDetails: true };
      })
      .addCase(fetchEvChargerWifiDetails.fulfilled, (state, { payload }) => {
        return { ...state, wifiSsid: payload.SSID };
      })
      .addCase(fetchEvChargerStatus.fulfilled, (state, action) => {
        return { ...state, ...action.payload, isLoaded: state.pendingCommandFromStatus === null };
      })
      .addCase(fetchEvChargerStatus.rejected, (state) => {
        return { ...state, isLoaded: state.pendingCommandFromStatus === null };
      })
      .addCase(deactivateEvCharger.pending, (state) => {
        return { ...state, isDeactivating: true };
      })
      .addCase(deactivateEvCharger.fulfilled, () => {
        return { ...initialState, isDeactivating: false, isLoaded: true };
      })
      .addCase(deactivateEvCharger.rejected, (state) => {
        return { ...state, isDeactivating: false, isLoaded: true };
      })
      .addCase(startCharging.pending, (state) => {
        return {
          ...state,
          isUpdating: true,
          isLoaded: false,
          pendingCommandFromStatus: state.status,
          pendingCommandDeadline: Date.now() + COMMAND_CONFIRMATION_TIMEOUT_MS,
        };
      })
      .addCase(startCharging.fulfilled, (state) => {
        return { ...state, isUpdating: false };
      })
      .addCase(startCharging.rejected, (state) => {
        return {
          ...state,
          isUpdating: false,
          isLoaded: true,
          pendingCommandFromStatus: null,
          pendingCommandDeadline: null,
        };
      })
      .addCase(stopCharging.pending, (state) => {
        return {
          ...state,
          isUpdating: true,
          isLoaded: false,
          pendingCommandFromStatus: state.status,
          pendingCommandDeadline: Date.now() + COMMAND_CONFIRMATION_TIMEOUT_MS,
        };
      })
      .addCase(stopCharging.fulfilled, (state) => {
        return { ...state, isUpdating: false };
      })
      .addCase(stopCharging.rejected, (state) => {
        return {
          ...state,
          isUpdating: false,
          isLoaded: true,
          pendingCommandFromStatus: null,
          pendingCommandDeadline: null,
        };
      });
  },
});

export const {
  resetEvChargerData,
  setIsLoaded,
  setSiteDeviceId,
  addEnrolledEvChargerDetails,
  setHasVisitedRegisterScreen,
  setChargeSimulationType,
  setFirmwareSimulationType,
  setTariffSimulationActive,
  setDeferralType,
  setEvseType,
  setEvseVariant,
  updateStatusFromSse,
  commandConfirmationTimedOut,
  updateControlModeFromSse,
} = evChargerSlice.actions;

export const selectEvChargerData = (state: RootState) => {
  return state.evCharger;
};

export const selectChargeSimulationType = (state: RootState) => {
  return state.evCharger.chargeSimulationType;
};

export const selectTariffSimulationActive = (state: RootState) => {
  return state.evCharger.tariffSimulationActive;
};

export const selectDeferralType = (state: RootState) => {
  return state.evCharger.deferralType;
};

export const selectFirmwareSimulationType = (state: RootState) => {
  return state.evCharger.firmwareSimulationType;
};

export const selectEvseType = (state: RootState) => {
  return state.evCharger.evseType;
};

export const selectEvseVariant = (state: RootState) => {
  return state.evCharger.evseVariant;
};

export const selectPendingCommandFromStatus = (state: RootState) => {
  return state.evCharger.pendingCommandFromStatus;
};
export const selectPendingCommandDeadline = (state: RootState) => {
  return state.evCharger.pendingCommandDeadline;
};

export const { reducer: evChargerReducer } = evChargerSlice;
